import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Chrome";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useCart } from "../lib/cart";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { unitSummary, PaymentBadge, TAX_NOTICE_ELEC, TAX_NOTICE_CASH, CONFIRMATION_DISCLAIMER } from "../lib/orderDisplay";

export default function Checkout() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { cart, totals, clear, paymentBreakdown } = useCart();
  const nav = useNavigate();
  const currency = settings?.currency || "$";
  const canOrder = settings?.ordering_enabled && user && user.role === "retailer" && user.status === "approved";

  const [delivery, setDelivery] = useState("delivery");
  const [contactName, setContactName] = useState(user?.contact_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // Structured server-side errors: [{product_id, name, requested, available, message, reason}]
  const [problems, setProblems] = useState([]);

  useEffect(() => {
    if (canOrder && cart.items.length === 0) nav("/cart", { replace: true });
  }, [cart.items.length, canOrder, nav]);

  if (!canOrder) {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <Header />
        <div className="pt-28 md:pt-32 px-6 md:px-12 lg:px-16 pb-16 max-w-3xl">
          <h1 className="font-display text-4xl md:text-6xl uppercase">Checkout unavailable.</h1>
          <p className="mt-4 text-sm text-black/60">Please log in as an approved retailer or contact us for a manual quote.</p>
          <Link to="/catalog" className="btn-primary mt-6 inline-block">Back to catalog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const submit = async () => {
    if (!contactName.trim()) { toast.error("Contact name is required"); return; }
    if (!phone.trim()) { toast.error("Phone is required"); return; }
    setBusy(true);
    setProblems([]);
    try {
      const { data } = await api.post("/orders", {
        lines: cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        notes, delivery_method: delivery,
        contact_name: contactName, phone,
      });
      clear();
      toast.success(`Order #${data.inquiry_no} placed`);
      nav(`/order/${data.id}/print`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured availability errors from the server: don't clear the cart.
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setProblems(detail.errors);
        toast.error("Some items in your cart can't be ordered — please review below.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(formatError(detail));
      }
    } finally { setBusy(false); }
  };

  const { cashItems, elecItems, cashTotal, elecTotal } = paymentBreakdown;

  const reviewSection = (label, rows, subtotal, testid, tax = false) => {
    if (rows.length === 0) return null;
    return (
      <div className="border border-ink bg-paper" data-testid={testid}>
        <div className="p-3 bg-secondary border-b border-ink flex items-center justify-between">
          <span className="overline">{label}</span>
          <PaymentBadge method={testid === "review-cash" ? "cash" : "electronic_or_cheque"} />
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/20 text-[10px] uppercase tracking-widest text-black/60">
              <th className="text-left p-3">Product</th>
              <th className="text-right p-3">Qty</th>
              <th className="text-right p-3">Unit</th>
              <th className="text-right p-3">Line total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(i => (
              <tr key={i.product_id} className="border-b border-black/10 align-top" data-testid={`${testid}-line-${i.product_id}`}>
                <td className="p-3">
                  <div className="font-bold">{i.name}</div>
                  {unitSummary(i) && <div className="text-[11px] text-black/60 mt-0.5">{unitSummary(i)}</div>}
                </td>
                <td className="p-3 text-right">{i.quantity}</td>
                <td className="p-3 text-right">{currency}{i.wholesale_price.toFixed(2)}</td>
                <td className="p-3 text-right font-bold">{currency}{(i.wholesale_price * i.quantity).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink bg-secondary">
              <td colSpan="3" className="p-3 text-right font-bold uppercase text-xs tracking-widest">{label} subtotal</td>
              <td className="p-3 text-right font-display text-xl" data-testid={`${testid}-subtotal`}>{currency}{subtotal.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
        {tax
          ? <div className="p-3 text-[11px] text-safety font-bold border-t border-black/10" data-testid={`${testid}-tax-notice`}>⚑ {TAX_NOTICE_ELEC}</div>
          : <div className="p-3 text-[11px] text-black/60 border-t border-black/10">{TAX_NOTICE_CASH}</div>
        }
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <div className="pt-28 md:pt-32 px-6 md:px-12 lg:px-16 pb-16" data-testid="checkout-page">
        <div className="overline text-black/50 mb-2">Confirm & submit</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">Checkout.</h1>

        {problems.length > 0 && (
          <div className="mt-6 border-2 border-soda bg-soda/5 p-4 md:p-5" data-testid="checkout-errors">
            <div className="font-bold text-soda uppercase tracking-widest text-sm mb-2">Please fix the following before placing the order</div>
            <ul className="space-y-1 text-sm">
              {problems.map((p, i) => (
                <li key={i} className="flex gap-2" data-testid={`checkout-error-${p.product_id || i}`}>
                  <span className="text-soda">•</span>
                  <span><b>{p.name}</b>: {p.message || `${p.requested} requested, ${p.available} available`}</span>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-black/60 mt-2">Your cart has been kept — update the quantities in the cart and try again.</div>
            <Link to="/cart" className="btn-ghost mt-3 inline-block" data-testid="checkout-errors-cart">← Back to cart</Link>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <div className="border border-ink p-5 md:p-6 bg-paper">
              <div className="overline mb-4">Delivery method</div>
              <div className="grid grid-cols-2 gap-3">
                {[["delivery", "Delivery to store"], ["pickup", "Pickup at warehouse"]].map(([v, label]) => (
                  <label key={v} data-testid={`checkout-delivery-${v}`} className={`border p-3 cursor-pointer text-sm ${delivery === v ? "bg-ink text-paper border-ink" : "border-black/20"}`}>
                    <input type="radio" name="delivery" value={v} className="hidden" checked={delivery === v} onChange={() => setDelivery(v)} />
                    <div className="font-bold uppercase text-[11px] tracking-widest">{label}</div>
                  </label>
                ))}
              </div>
              {settings?.free_delivery_amount > 0 && delivery === "delivery" && (
                <div className="text-[11px] text-black/50 mt-3">Free delivery on orders over {currency}{settings.free_delivery_amount}.</div>
              )}
            </div>

            <div className="border border-ink p-5 md:p-6 bg-paper">
              <div className="overline mb-4">Contact for this order</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Contact name</label>
                  <input value={contactName} onChange={e => setContactName(e.target.value)} className="field" data-testid="checkout-contact-name" />
                </div>
                <div>
                  <label className="overline block mb-2">Phone</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} className="field" data-testid="checkout-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Order notes (optional)</label>
                  <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} className="field" placeholder="Delivery date preference, dock hours, etc." data-testid="checkout-notes" />
                </div>
              </div>
            </div>

            {/* Split review sections — always show both if applicable. */}
            <div className="space-y-6" data-testid="checkout-review">
              <div className="overline">Final review — please confirm</div>
              {reviewSection("Cash", cashItems, cashTotal, "review-cash", false)}
              {reviewSection("E-Transfer / Cheque", elecItems, elecTotal, "review-elec", true)}
            </div>

            <div className="border-2 border-ink bg-secondary p-4 md:p-5 text-sm leading-relaxed" data-testid="checkout-disclaimer">
              {CONFIRMATION_DISCLAIMER}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-ink p-5 md:p-6 sticky top-24 bg-paper hard-shadow" data-testid="checkout-summary">
              <div className="overline mb-4">Summary</div>
              {cashItems.length > 0 && (
                <div className="flex justify-between text-sm mb-2"><span>Cash total</span><span className="font-bold" data-testid="summary-cash-total">{currency}{cashTotal.toFixed(2)}</span></div>
              )}
              {elecItems.length > 0 && (
                <div className="flex justify-between text-sm mb-2"><span>E-Transfer / Cheque</span><span className="font-bold" data-testid="summary-elec-total">{currency}{elecTotal.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-lg font-bold border-t-2 border-ink pt-3 mt-3">
                <span>Combined (before tax)</span>
                <span data-testid="checkout-subtotal">{currency}{totals.subtotal.toFixed(2)}</span>
              </div>
              <button disabled={busy} onClick={submit}
                      className="btn-primary w-full mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="checkout-submit">
                {busy ? "Placing order…" : "Place order →"}
              </button>
              <Link to="/cart" className="btn-ghost w-full mt-3 block text-center" data-testid="checkout-back">Back to cart</Link>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
